import type { AlwaysAiUser } from "../../../shared/api-types";
import {
  readStoredValue,
  writeStoredValue,
  type LookStorage,
  type ShellLook,
} from "./look-preference";
import { rendersAlwaysFrame } from "./workspace-frame";

// The always theme on <html> (NO-790). @ndd/ui's [data-theme="always"] block
// restyles every token, and it must reach pages outside the workspace frame
// too (admin pages, the 404, sign-in, dialogs and popovers portaled into
// <body>), so the attribute sits on <html>, never on a shell.
//
// Three writers keep it right:
// - The inline script in client/index.html sets it before first paint from
//   THEME_CACHE_KEY, so a returning flagged viewer never sees the classic look
//   flash. Clerk has not loaded at that point, so the cache is per browser,
//   and its value is the Clerk id of the viewer it was written for.
// - The root route's layout effect (lib/use-always-theme.ts) corrects it once
//   Clerk resolves, and again once flags and /api/me settle, and refreshes
//   the cache. The settled answer is rendersAlwaysFrame(), the same predicate
//   the workspace frame uses, never resolveLook() alone: a flagged VA, or an
//   RD without rd-home, renders the legacy chrome and keeps the classic look.
// - The look switch writes the stored look; the root re-renders from the same
//   store and the layout effect flips the attribute before the next paint.
//
// Signed out, nothing changes: the sign-in page keeps the look of the last
// viewer on this browser. Values are a Clerk id or nothing (never PHI).
//
// Always AI sends no Content-Security-Policy (helmet's is off in
// server/app.ts). A future CSP must allow the inline script by its hash.

/** Must match the inline script in client/index.html (always-theme.test.ts reads both). */
export const THEME_CACHE_KEY = "alwaysai:theme";
export const THEME_ATTRIBUTE = "data-theme";
export const ALWAYS_THEME = "always";

export interface AlwaysThemeInput {
  signedIn: boolean;
  clerkId: string | null;
  featuresPending: boolean;
  flagOn: boolean;
  mePending: boolean;
  user: AlwaysAiUser | undefined;
  rdHomeEnabled: boolean;
  /** Already resolved against the flag: "classic" whenever it is off. */
  look: ShellLook;
  /** This viewer's last-rendered look (lib/look-preference.ts), or null. */
  lastLook: ShellLook | null;
  /** The Clerk id THEME_CACHE_KEY holds, or null. */
  cachedOwner: string | null;
}

/**
 * `on`: whether <html> carries the theme. `cache`: what to do with
 * THEME_CACHE_KEY ("keep" while flags or the viewer are still loading).
 */
export interface AlwaysThemeDecision {
  on: boolean;
  cache: "write" | "clear" | "keep";
}

// While flags or /api/me load, hold the theme only for the viewer it was
// cached for, or one whose last-rendered frame was the rail: a different
// person signing in on a shared browser drops a previous viewer's look as
// soon as Clerk knows who they are.
function holdsTheme(input: AlwaysThemeInput, clerkId: string): boolean {
  const cachedForViewer = input.cachedOwner === clerkId;

  return cachedForViewer || input.lastLook === ALWAYS_THEME;
}

function settledOn(input: AlwaysThemeInput): boolean {
  if (input.user === undefined) return false;

  return rendersAlwaysFrame(input.user, input.look, input.rdHomeEnabled);
}

/** The theme for this render, or null to leave <html> and the cache alone. */
export function decideAlwaysTheme(input: AlwaysThemeInput): AlwaysThemeDecision | null {
  const clerkId = input.clerkId;
  if (!input.signedIn || clerkId === null) return null;

  if (input.featuresPending) return { on: holdsTheme(input, clerkId), cache: "keep" };

  // The flag always wins, and a failed flag read counts as off.
  if (!input.flagOn) return { on: false, cache: "clear" };

  if (input.mePending) return { on: holdsTheme(input, clerkId), cache: "keep" };

  const on = settledOn(input);

  return { on, cache: on ? "write" : "clear" };
}

/** The slice of <html> the theme touches; a plain object in tests. */
export interface ThemeRoot {
  getAttribute(name: string): string | null;
  setAttribute(name: string, value: string): void;
  removeAttribute(name: string): void;
}

/** Sets or clears data-theme="always"; never removes another theme's value. */
export function applyAlwaysTheme(root: ThemeRoot, on: boolean): void {
  const current = root.getAttribute(THEME_ATTRIBUTE);

  if (on) {
    if (current !== ALWAYS_THEME) root.setAttribute(THEME_ATTRIBUTE, ALWAYS_THEME);

    return;
  }

  if (current === ALWAYS_THEME) root.removeAttribute(THEME_ATTRIBUTE);
}

export function readThemeCacheOwner(storage: LookStorage | null): string | null {
  const stored = readStoredValue(storage, THEME_CACHE_KEY);

  return stored === "" ? null : stored;
}

export function updateThemeCache(
  storage: LookStorage | null,
  cache: AlwaysThemeDecision["cache"],
  clerkId: string
): void {
  if (cache === "keep") return;

  writeStoredValue(storage, THEME_CACHE_KEY, cache === "write" ? clerkId : null);
}
